import {
  ComBoxStatus,
  getCurrentRoomSensorData,
  getCurrentSensorData,
  getSensorTypes,
  isMonitorProgramOfComBox,
} from "@/data/models/ComBox";
import {
  ComBoxSensorType,
  ROOM_SENSOR_INDEX,
  isBleResistanceSensorType,
} from "@/data/models/ComBoxSensorData";
import { isGuideSensorSet } from "@/data/models/SampleInfo";
import {
  ProgramChain,
  getKeySensorDataOfComBoxRuns,
  getKeySensorValuesOfPanelProgramChain,
  getPerSensorRunData,
} from "@/data/models/ProgramChain";
import { PanelStatus } from "@/data/models/Panel";
import { RunOverview } from "@/data/models/RunOverview";
import {
  getHumidityValue,
  getComBoxHumidityUnitFormatRes,
} from "@/data/models/ComBoxHumidityUnit";
import { getComBoxHumidityUnit } from "@/data/repository/SettingsRepository";

export enum SensorDataKeyValue {
  AVERAGE,
  MAX,
  MIN,
}

export class MinMaxKeyValueAggregator {
  min = Number.MAX_SAFE_INTEGER;
  max = Number.MIN_SAFE_INTEGER;

  valueSum = 0;
  numValues = 0;

  firstValue: number | null = null;
  lastValue: number | null = null;

  captureFirstAndLastValue = false;

  filter: (value: number) => boolean;

  constructor(
    filter: (value: number) => boolean,
    captureFirstAndLastValue?: boolean
  ) {
    this.filter = filter;
    this.captureFirstAndLastValue = captureFirstAndLastValue ?? false;
  }

  add(minMaxKeyValue: MinMaxKeyValue) {
    this.addMinMaxValue(
      minMaxKeyValue.min,
      minMaxKeyValue.max,
      minMaxKeyValue.keyValue
    );
  }

  reset() {
    this.min = Number.MAX_SAFE_INTEGER;
    this.max = Number.MIN_SAFE_INTEGER;
    this.valueSum = 0;
    this.numValues = 0;
  }

  addValue(value?: number) {
    if (value === undefined) {
      return;
    }

    this.addMinMaxValue(value, value, value);
  }

  addMinMaxValue(min?: number, max?: number, value?: number) {
    if (min !== undefined && this.filter(min)) {
      this.min = Math.min(this.min, min);
    }

    if (max !== undefined && this.filter(max)) {
      this.max = Math.max(this.max, max);
    }

    if (value !== undefined && this.filter(value)) {
      this.valueSum += value;
      this.numValues++;

      if (this.captureFirstAndLastValue) {
        if (this.firstValue === null) {
          this.firstValue = value;
        }

        this.lastValue = value;
      }
    }
  }

  toMinMaxKeyValue(keyValue?: SensorDataKeyValue): MinMaxKeyValue {
    const adjustedKeyValue = keyValue ?? SensorDataKeyValue.AVERAGE;

    if (this.numValues == 0) {
      return {};
    }

    const value =
      adjustedKeyValue === SensorDataKeyValue.MIN
        ? this.min
        : adjustedKeyValue === SensorDataKeyValue.MAX
        ? this.max
        : this.valueSum / this.numValues;

    return {
      min: this.min,
      max: this.max,
      keyValue: value,
    };
  }
}

export interface MinMaxKeyValue {
  min?: number;
  max?: number;
  keyValue?: number;
}

export function formatMinMaxKeyValue(
  vue: Vue,
  stringFormat: string,
  min: number,
  max: number,
  keyValue?: number
) {
  const formattedMin = Math.round(min).toString();
  const formattedMax = Math.round(max).toString();

  if (keyValue === undefined) {
    if (formattedMax == formattedMin) {
      return vue.$t(stringFormat, [formattedMax]);
    } else {
      return vue.$t(stringFormat, [`${formattedMin} - ${formattedMax}`]);
    }
  } else {
    const formattedKey = vue.$t(stringFormat, [
      Math.round(keyValue).toString(),
    ]);

    if (formattedMax == formattedMin) {
      return formattedKey;
    } else {
      return `${formattedKey} (${formattedMin} - ${formattedMax})`;
    }
  }
}

export function formatPanelSensorData(
  vue: Vue,
  format: string,
  minMaxKeyValue: MinMaxKeyValue,
  filter: (value: number | undefined) => boolean,
  currentValue?: number
) {
  const aggregator = new MinMaxKeyValueAggregator(filter);

  aggregator.add(minMaxKeyValue);

  const doesCurrentValueMatchFilter = filter(currentValue);

  if (doesCurrentValueMatchFilter) {
    aggregator.addValue(currentValue);
  }

  const { min, max } = aggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX);

  if (min === undefined || max === undefined) {
    return "-";
  }

  return formatMinMaxKeyValue(
    vue,
    format,
    min,
    max,
    doesCurrentValueMatchFilter ? currentValue : undefined
  );
}

export function formatWallTemp(
  vue: Vue,
  panelStatus?: PanelStatus,
  minMaxKeyValue?: MinMaxKeyValue
) {
  return formatPanelSensorData(
    vue,
    "panelDetail.formatTemperatureWall",
    minMaxKeyValue ?? {},
    (value: number | undefined) => {
      return value != 255;
    },
    panelStatus?.currentWallTemp
  );
}

export function formatGroundTemp(
  vue: Vue,
  panelStatus?: PanelStatus,
  minMaxKeyValue?: MinMaxKeyValue
) {
  return formatPanelSensorData(
    vue,
    "panelDetail.formatTemperatureGround",
    minMaxKeyValue ?? {},
    (value: number | undefined) => {
      return value != 255;
    },
    panelStatus?.currentChipTemp
  );
}

export function formatIntensity(
  vue: Vue,
  panelStatus?: PanelStatus,
  minMaxKeyValue?: MinMaxKeyValue
) {
  return formatPanelSensorData(
    vue,
    "panelDetail.formatHeatRatioWithLabel",
    minMaxKeyValue ?? {},
    (value: number | undefined) => {
      return value !== undefined && value > 0 && value <= 100;
    },
    panelStatus?.rateStepHeatCool
  );
}

export function getPanelSensorData(
  panelStatus?: PanelStatus,
  programChain?: ProgramChain
) {
  if (!panelStatus && !programChain) {
    undefined;
  }

  const groundTempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const wallTempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const intensityAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value > 0 && value <= 100
  );

  if (panelStatus) {
    groundTempAggregator.addValue(panelStatus.currentChipTemp);
    wallTempAggregator.addValue(panelStatus.currentWallTemp);
    intensityAggregator.addValue(panelStatus.rateStepHeatCool);
  }

  if (programChain) {
    const {
      groundTemp,
      wallTemp,
      intensity,
    } = getKeySensorValuesOfPanelProgramChain(programChain);

    groundTempAggregator.add(groundTemp);
    wallTempAggregator.add(wallTemp);
    intensityAggregator.add(intensity);
  }

  return {
    groundTemp: groundTempAggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX),
    wallTemp: wallTempAggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX),
    intensity: intensityAggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX),
  };
}

export function formatComBoxRunSensorData(
  vue: Vue,
  roomSensors: boolean,
  comBoxStatus?: ComBoxStatus,
  runs?: RunOverview[]
) {
  if (!comBoxStatus && (!runs || runs.length === 0)) {
    return "-";
  }

  const humidityUnit = getComBoxHumidityUnit();

  const tempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const humidityAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );

  let currentTempKeyValue: number | undefined = undefined;
  let currentHumidityKeyValue: number | undefined = undefined;

  if (comBoxStatus) {
    const tempCurrent = new MinMaxKeyValueAggregator(
      (value) => value !== undefined && value !== 255
    );
    const humidityCurrent = new MinMaxKeyValueAggregator(
      (value) => value !== undefined && value !== 255
    );

    if (roomSensors) {
      getCurrentRoomSensorData(comBoxStatus).forEach((value) => {
        tempCurrent.addValue(value.temperature);
        humidityCurrent.addValue(getHumidityValue(humidityUnit, value));
      });
    } else {
      if (comBoxStatus.deviceState === 1 || isMonitorProgramOfComBox(comBoxStatus.activeProgram)) {
        const allSensorData = getCurrentSensorData(comBoxStatus);
        const sensorTypes = getSensorTypes(comBoxStatus);

        allSensorData.forEach((sensorData, index) => {
          if (index === ROOM_SENSOR_INDEX) return;
          const sensorType = sensorTypes[index];
          if (!sensorType || sensorType === ComBoxSensorType.EMPTY) return;

          const temp = sensorData.temperature;
          const humidity = sensorData.relativeHumidity;

          if (humidity !== undefined && humidity !== 0 && humidity !== Number.MIN_SAFE_INTEGER) {
            tempCurrent.addValue(temp);
            humidityCurrent.addValue(
              getHumidityValue(humidityUnit, sensorData)
            );
          }
        });
      } else {
        const allSensorData = getCurrentSensorData(comBoxStatus);
        const guideValue = allSensorData[comBoxStatus.currentGuideSensor];

        if (
          guideValue &&
          guideValue.relativeHumidity !== Number.MIN_SAFE_INTEGER
        ) {
          tempCurrent.addValue(guideValue.temperature);
          humidityCurrent.addValue(getHumidityValue(humidityUnit, guideValue));
        }
      }
    }

    currentTempKeyValue = tempCurrent.toMinMaxKeyValue(
      SensorDataKeyValue.AVERAGE
    ).keyValue;

    currentHumidityKeyValue = humidityCurrent.toMinMaxKeyValue(
      SensorDataKeyValue.MAX
    ).keyValue;

    tempAggregator.addValue(currentTempKeyValue);
    humidityAggregator.addValue(currentHumidityKeyValue);
  }

  if (runs) {
    const { temp, humidity } = getKeySensorDataOfComBoxRuns(
      runs,
      roomSensors,
      humidityUnit
    );

    tempAggregator.addMinMaxValue(temp.min, temp.max);
    humidityAggregator.addMinMaxValue(humidity.min, humidity.max);
  }

  const tempMin = tempAggregator.min;
  const tempMax = tempAggregator.max;

  const humidityMin = humidityAggregator.min;
  const humidityMax = humidityAggregator.max;

  if (tempMin === Number.MAX_SAFE_INTEGER) {
    return "-";
  }

  if (
    tempMin === undefined ||
    tempMax === undefined ||
    humidityMin === undefined ||
    humidityMax === undefined
  ) {
    return "-";
  }

  const formattedTemp = formatMinMaxKeyValue(
    vue,
    "common.formatTemperature",
    tempMin,
    tempMax,
    currentTempKeyValue
  );

  const formattedHumidity = formatMinMaxKeyValue(
    vue,
    getComBoxHumidityUnitFormatRes(humidityUnit),
    humidityMin,
    humidityMax,
    currentHumidityKeyValue
  );

  return `${formattedTemp} / ${formattedHumidity}`;
}

export function formatComBoxSensorData(
  vue: Vue,
  roomSensors: boolean,
  comBoxStatus?: ComBoxStatus,
  programChain?: ProgramChain
) {
  return formatComBoxRunSensorData(
    vue,
    roomSensors,
    comBoxStatus,
    programChain?.runs
  );
}

/**
 * Determines which external sensor index to display in the floor temp/humidity row.
 * - If the guide sensor is valid and not the room sensor, returns the guide sensor index.
 * - Otherwise, returns the external sensor with the highest relative humidity.
 * - Returns null if no external sensors have data.
 */
export function getFloorDisplaySensorIndex(comBoxStatus?: ComBoxStatus): number | null {
  if (!comBoxStatus) return null;

  const allSensorData = getCurrentSensorData(comBoxStatus);
  const sensorTypes = getSensorTypes(comBoxStatus);

  const guideSensor = comBoxStatus.currentGuideSensor;

  if (
    isGuideSensorSet(guideSensor) &&
    guideSensor !== ROOM_SENSOR_INDEX &&
    sensorTypes[guideSensor] &&
    sensorTypes[guideSensor] !== ComBoxSensorType.EMPTY
  ) {
    return guideSensor;
  }

  let bestIndex: number | null = null;
  let bestHumidity = -Infinity;

  allSensorData.forEach((data, index) => {
    if (index === ROOM_SENSOR_INDEX) return;

    const sensorType = sensorTypes[index];
    if (!sensorType || sensorType === ComBoxSensorType.EMPTY) return;

    const humidity = data.relativeHumidity;
    if (
      humidity !== undefined &&
      humidity !== Number.MIN_SAFE_INTEGER &&
      humidity !== 0 &&
      humidity > bestHumidity
    ) {
      bestHumidity = humidity;
      bestIndex = index;
    }
  });

  return bestIndex;
}

/**
 * Determines which material (Widerstand) sensor index to display.
 * Mirrors {@link getFloorDisplaySensorIndex}:
 * - If the guide sensor is valid, not the room sensor, and is a material sensor, returns it.
 *   (A material sensor is not normally a guide sensor; this branch is kept for symmetry.)
 * - Otherwise returns the material sensor with the highest resistance digits
 *   (the mirror of "highest humidity" — a higher value indicates a wetter material).
 * - Returns null if no material sensor has resistance data.
 */
export function getMaterialDisplaySensorIndex(comBoxStatus?: ComBoxStatus): number | null {
  if (!comBoxStatus) return null;

  const allSensorData = getCurrentSensorData(comBoxStatus);
  const sensorTypes = getSensorTypes(comBoxStatus);

  const guideSensor = comBoxStatus.currentGuideSensor;

  if (
    isGuideSensorSet(guideSensor) &&
    guideSensor !== ROOM_SENSOR_INDEX &&
    isBleResistanceSensorType(sensorTypes[guideSensor])
  ) {
    return guideSensor;
  }

  let bestIndex: number | null = null;
  let bestResistanceDigits = -Infinity;

  allSensorData.forEach((data, index) => {
    if (index === ROOM_SENSOR_INDEX) return;
    if (!isBleResistanceSensorType(sensorTypes[index])) return;

    const resistanceDigits = data.resistanceDigits;
    if (resistanceDigits !== undefined && resistanceDigits > bestResistanceDigits) {
      bestResistanceDigits = resistanceDigits;
      bestIndex = index;
    }
  });

  return bestIndex;
}

/**
 * Formats temperature and humidity for a single sensor index.
 * Shows the sensor's current reading (if comBoxStatus is provided) and
 * historical min/max range from runs filtered to that specific sensor.
 */
export function formatSingleSensorData(
  vue: Vue,
  sensorIndex: number,
  comBoxStatus?: ComBoxStatus,
  runs?: RunOverview[]
): string {
  if (!comBoxStatus && (!runs || runs.length === 0)) {
    return "-";
  }

  const humidityUnit = getComBoxHumidityUnit();

  const tempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const humidityAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );

  let currentTempKeyValue: number | undefined;
  let currentHumidityKeyValue: number | undefined;

  if (comBoxStatus) {
    const allSensorData = getCurrentSensorData(comBoxStatus);
    const sensorData = allSensorData[sensorIndex];

    if (
      sensorData &&
      sensorData.relativeHumidity !== undefined &&
      sensorData.relativeHumidity !== Number.MIN_SAFE_INTEGER
    ) {
      currentTempKeyValue = sensorData.temperature;
      currentHumidityKeyValue = getHumidityValue(humidityUnit, sensorData);

      tempAggregator.addValue(currentTempKeyValue);
      humidityAggregator.addValue(currentHumidityKeyValue);
    }
  }

  if (runs && runs.length > 0) {
    const { temp, humidity } = getPerSensorRunData(runs, sensorIndex, humidityUnit);

    tempAggregator.addMinMaxValue(temp.min, temp.max);
    humidityAggregator.addMinMaxValue(humidity.min, humidity.max);
  }

  const tempMin = tempAggregator.min;
  const tempMax = tempAggregator.max;

  const humidityMin = humidityAggregator.min;
  const humidityMax = humidityAggregator.max;

  if (tempMin === Number.MAX_SAFE_INTEGER) {
    return "-";
  }

  if (
    tempMin === undefined ||
    tempMax === undefined ||
    humidityMin === undefined ||
    humidityMax === undefined
  ) {
    return "-";
  }

  const formattedTemp = formatMinMaxKeyValue(
    vue,
    "common.formatTemperature",
    tempMin,
    tempMax,
    currentTempKeyValue
  );

  const formattedHumidity = formatMinMaxKeyValue(
    vue,
    getComBoxHumidityUnitFormatRes(humidityUnit),
    humidityMin,
    humidityMax,
    currentHumidityKeyValue
  );

  return `${formattedTemp} / ${formattedHumidity}`;
}
